import React, { useState } from 'react';

import { Icon, RadioButton, TextInput } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { ModalContent, ModalHeading } from 'components/modal';

import {
  Description,
  IconContainer,
  InputContainer,
  ListItem,
  ListItemLabel,
  ListWrapper,
  ScheduleForm,
  ScheduleInputWrapper,
  StyledButtonGroup,
  StyledDialog,
  TextLabel,
} from './date-time-schedule-modal.styled';
import { IDateTimeScheduleModalProps, TSchedulerDate, TSchedulerTimeList } from './types';

const DateTimeScheduleModal: React.FC<IDateTimeScheduleModalProps> = ({
  dateTime,
  onDismiss,
  onConfirm,
  onSchedule,
}) => {
  const [showDateList, setShowDateList] = useState(false);
  const [showTimeList, setShowTimeList] = useState(false);
  const [dates, setDates] = useState<TSchedulerDate[]>([]);
  const [selectedDate, setSelectedDate] = useState<TSchedulerDate | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectTimeEnabled, setSelectTimeEnabled] = useState(false);
  const [timeValues, setTimeValues] = useState<TSchedulerTimeList>([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const { formatMessage, formatDate } = useIntl();

  const scheduleOrder = () => {
    onSchedule?.(`${selectedDate?.value ?? ''} ${selectedTime}`);
    onConfirm?.();
  };

  const displayDateContainer = () => {
    setShowTimeList(false);
    setShowDateList(true);
    setDates(Array.from(dateTime.keys()));
  };

  const displayTimeContainer = () => {
    const timeListForDate = selectedDate ? dateTime.get(selectedDate) : [];
    setShowDateList(false);
    setShowTimeList(true);
    setTimeValues(timeListForDate ?? []);
  };

  const onDateSelect = (newDate: TSchedulerDate) => {
    setSelectedTime('');
    setIsFormValid(false);
    setSelectedDate(newDate);
    setSelectTimeEnabled(true);
  };

  const onTimeSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTime(event.target.value);
    setIsFormValid(true);
  };

  return (
    <StyledDialog onDismiss={onDismiss} data-testid="schedule-modal">
      <ModalContent>
        <ModalHeading>
          <IconContainer>
            <Icon icon="time" color="icon-default" width="24px" aria-hidden />
          </IconContainer>
          <span>{formatMessage({ id: 'scheduleOrder' })}</span>
        </ModalHeading>
        <ScheduleForm>
          <ScheduleInputWrapper>
            <InputContainer>
              <TextLabel>{formatMessage({ id: 'selectDate' })}</TextLabel>
              <TextInput
                value={
                  selectedDate?.value
                    ? formatDate(selectedDate?.value, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : ''
                }
                autoFocus={false}
                autoComplete="off"
                type="text"
                onFocus={displayDateContainer}
                readOnly
                label=""
                icon={<Icon icon="calendar" color="icon-default" width="24px" aria-hidden />}
                placeholderChar={formatMessage({ id: 'selectDate' })}
                data-testid="date-input"
                aria-label={formatMessage({ id: 'selectDate' })}
              />
            </InputContainer>
            <InputContainer disabled={!selectTimeEnabled}>
              <TextLabel>{formatMessage({ id: 'selectTime' })}</TextLabel>
              <TextInput
                value={selectedTime}
                autoFocus={false}
                autoComplete="off"
                type="text"
                onFocus={displayTimeContainer}
                readOnly
                label=""
                icon={<Icon icon="time" color="icon-default" width="24px" aria-hidden />}
                disabled={!selectTimeEnabled}
                placeholderChar={formatMessage({ id: 'selectTime' })}
                data-testid="time-input"
                aria-label={formatMessage({ id: 'selectTime' })}
              />
            </InputContainer>
          </ScheduleInputWrapper>

          {showDateList && (
            <div>
              <ListWrapper data-testid="date-list">
                {dates.map((date: TSchedulerDate) => {
                  const dateInputlabel = (
                    <div>
                      <ListItemLabel>
                        {formatDate(date.value, {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </ListItemLabel>
                      {date.description && (
                        <Description>{formatMessage({ id: date.description })}</Description>
                      )}
                    </div>
                  );
                  return (
                    <ListItem key={date.value} disabled={date.disabled}>
                      <RadioButton
                        checked={selectedDate?.value === date.value}
                        label={dateInputlabel}
                        value={date.value}
                        name="schedule-date"
                        onChange={() => onDateSelect(date)}
                        disabled={date.disabled}
                        data-testid="date-radio"
                      />
                    </ListItem>
                  );
                })}
              </ListWrapper>
            </div>
          )}
          {showTimeList && (
            <div>
              <ListWrapper data-testid="time-list">
                {timeValues.map((time: string) => {
                  const timeInputLabel = <ListItemLabel>{time}</ListItemLabel>;
                  return (
                    <ListItem key={time}>
                      <RadioButton
                        checked={selectedTime === time}
                        label={timeInputLabel}
                        value={time}
                        name="schedule-time"
                        onChange={onTimeSelect}
                        data-testid="time-radio"
                      />
                    </ListItem>
                  );
                })}
              </ListWrapper>
            </div>
          )}
        </ScheduleForm>
        <StyledButtonGroup>
          <ActionButton
            disabled={!isFormValid}
            onClick={scheduleOrder}
            fullWidth
            variant={ActionButtonVariants.PRIMARY}
            data-testid="schedule-button"
          >
            {formatMessage({ id: 'schedule' })}
          </ActionButton>
          <ActionButton
            onClick={onDismiss}
            fullWidth
            variant={ActionButtonVariants.OUTLINE}
            data-testid="cancel-button"
          >
            {formatMessage({ id: 'cancel' })}
          </ActionButton>
        </StyledButtonGroup>
      </ModalContent>
    </StyledDialog>
  );
};

export default DateTimeScheduleModal;
